import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../styles/test-system.css';
import './TestCatalog.css';
import Navigation from '../components/Navigation';
import TestNav from '../components/tests/TestNav';
import Countdown, { useNow } from '../components/tests/Countdown';
import CycleStatusChip from '../components/tests/CycleStatusChip';
import EnrolmentStatusChip from '../components/tests/EnrolmentStatusChip';
import { getMyEnrolments, getMyEntries, getTestCatalog, readApiError } from '../api/testSystem';
import { LIVE_ENROLMENT_STATUSES } from '../lib/trialDoor';
import { isAuthenticated } from '../utils/auth';
import {
    cycleScheduleLine,
    formatMinutes,
    formatSpotsPhrase,
    formatTrialFormat,
    isCycleFull,
    isEnrolmentOpen,
} from '../utils/testCycle';

const ClockIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
    </svg>
);

/* Whether this enrolment still holds the spot. A `no_show` or a returned entry is
   an enrolment the candidate *had*: the dashboard and My Enrolments both say so, and
   the catalog was the one page still calling it "You're enrolled". */
const holdsSpot = (enrolment) => LIVE_ENROLMENT_STATUSES.includes(enrolment?.status);

const CycleCard = ({ cycle, enrolment, now }) => {
    // A cohort past its enrolment cannot be joined, so how many spots it has left
    // is no longer a fact about anything the candidate can do.
    const enrolmentOpen = isEnrolmentOpen(cycle, now);
    // Open with nothing left to take is not an invitation.
    const full = isCycleFull(cycle);
    const needsConfirming = cycle.status === 'confirmation' && enrolment?.status === 'enrolled';
    const onIt = holdsSpot(enrolment);

    return (
        <Link to={`/tests/cycles/${cycle.cycle_id}`} className="ts-card" data-testid="cycle-card">
            <div className="ts-card-head">
                <div className="ts-card-heading">
                    <h2 className="ts-card-title">{cycle.structure_name || 'Test'}</h2>
                </div>
                <CycleStatusChip status={cycle.status} enrolmentOpen={enrolmentOpen} full={full} />
            </div>

            {cycle.structure_description && (
                <p className="ts-card-desc">{cycle.structure_description}</p>
            )}

            <div className="ts-card-meta">
                <span className="ts-card-meta-item">
                    <ClockIcon />
                    {formatMinutes(cycle.version?.duration)}
                </span>
                <span className="ts-card-meta-item">{formatTrialFormat(cycle.version)}</span>
                <span className="ts-card-meta-item">{cycleScheduleLine(cycle)}</span>
                {enrolmentOpen && !full && (
                    <span className="ts-card-meta-item">{formatSpotsPhrase(cycle)}</span>
                )}
            </div>

            <div className="ts-card-foot">
                {enrolment && !onIt ? (
                    /* The spot is gone. Said in the same words My Enrolments says it
                       in, because two pages calling one standing two things is how a
                       student ends up at a cohort they are not on. */
                    <EnrolmentStatusChip status={enrolment.status} />
                ) : enrolment ? (
                    /* Inside the window an unconfirmed spot is the one thing on this card
                       that needs doing, and missing it costs the spot and the entry. */
                    <span className={`ts-chip ts-chip--${needsConfirming ? 'warn' : 'live'}`}>
                        <span className="ts-chip-dot" />
                        {needsConfirming
                            ? 'Confirm your spot'
                            : enrolment.status === 'confirmed' ? 'Participation confirmed' : "You're enrolled"}
                    </span>
                ) : !enrolmentOpen ? (
                    <span className="ts-card-deadline">Enrolment closed</span>
                ) : full ? (
                    <span className="ts-card-deadline">Every spot on this cycle is taken</span>
                ) : cycle.enrolment_cutoff_at ? (
                    <span className="ts-card-deadline">
                        Enrolment closes{' '}
                        <Countdown target={cycle.enrolment_cutoff_at} passedLabel="— closed" />
                    </span>
                ) : (
                    /* A manual-start cohort carries no cutoff, so there is no countdown
                       to run — enrolment stays open until the team starts it. */
                    <span className="ts-card-deadline">
                        Enrolment is open — the team picks the start date and emails you
                    </span>
                )}
                <span className="ts-card-cta">
                    View details
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 18l6-6-6-6" />
                    </svg>
                </span>
            </div>
        </Link>
    );
};

/* The catalog is served as one list of "cycles you may enrol on or watch", which
   mixes three different things to do. Grouped, each heading answers the only
   question the rows under it raise. */
const GROUPS = [
    { key: 'yours', title: 'Cycles you are on' },
    { key: 'open', title: 'Open for enrolment' },
    { key: 'closed', title: 'Not taking enrolments' },
];

const TestCatalog = () => {
    const navigate = useNavigate();
    const now = useNow();
    const [cycles, setCycles] = useState([]);
    const [enrolments, setEnrolments] = useState({});
    const [balance, setBalance] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const loadCatalog = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            setCycles(await getTestCatalog());
        } catch (e) {
            setError(readApiError(e, 'The test catalog could not be loaded.').message);
        } finally {
            setLoading(false);
        }
    }, []);

    // Balance and enrolments only decorate the list — a failure there must not
    // cost the candidate the catalog itself.
    const loadCandidateState = useCallback(async () => {
        try {
            const entries = await getMyEntries();
            setBalance(entries?.entries_balance ?? 0);
        } catch (e) {
            console.error('Error loading entries balance:', e);
        }

        try {
            const rows = await getMyEnrolments();
            setEnrolments(Object.fromEntries(
                rows.filter(row => row.cycle?.cycle_id).map(row => [row.cycle.cycle_id, row.enrolment])
            ));
        } catch (e) {
            console.error('Error loading enrolments:', e);
        }
    }, []);

    useEffect(() => {
        if (!isAuthenticated()) {
            navigate('/login');
            return;
        }

        loadCatalog();
        loadCandidateState();
    }, [navigate, loadCatalog, loadCandidateState]);

    const grouped = GROUPS
        .map(group => ({
            ...group,
            rows: cycles.filter((cycle) => {
                // Only a spot the candidate still holds puts a cohort under "Cycles
                // you are on". One they lost is listed where anybody else's is, and
                // its card says what happened to it.
                if (holdsSpot(enrolments[cycle.cycle_id])) return group.key === 'yours';
                return group.key === (isEnrolmentOpen(cycle, now) ? 'open' : 'closed');
            }),
        }))
        .filter(group => group.rows.length > 0);
    // One group needs no heading: the page title already says what the list is.
    const showHeadings = grouped.length > 1;

    return (
        <>
            <Navigation />
            <div className="ts-page">
                <div className="ts-shell ts-shell--wide">
                    <TestNav />

                    <header className="ts-head">
                        <div className="ts-head-main">
                            <h1 className="ts-title">Test catalog</h1>
                            <p className="ts-lede">
                                Work trials you can take for a certificate. Each one runs at a set time
                                with everyone else in the cycle: enrol while enrolment is open, confirm
                                your spot when we email you, then take it.
                            </p>
                        </div>
                        {balance !== null && (
                            <div className="ts-head-aside">
                                <Link className="ts-figure" to="/tests/entries" data-testid="catalog-balance">
                                    <span className="ts-figure-count">{balance}</span>{' '}
                                    <span className="ts-figure-label">
                                        {balance === 1 ? 'entry' : 'entries'} available
                                    </span>
                                </Link>
                            </div>
                        )}
                    </header>

                    {loading ? (
                        <div className="ts-page-state ts-page-state--loading" data-testid="catalog-loading">
                            <div className="ts-spinner" />
                            <p>Loading the catalog…</p>
                        </div>
                    ) : error ? (
                        <div className="ts-page-state" role="alert">
                            <h2>The catalog is unavailable</h2>
                            <p>{error}</p>
                            <button className="ts-btn" onClick={loadCatalog}>Try again</button>
                        </div>
                    ) : cycles.length === 0 ? (
                        <div className="ts-page-state" data-testid="catalog-empty">
                            <h2>No tests are open right now</h2>
                            <p>
                                Cycles open on a schedule. Check back later — anything you can enrol
                                on will show up here.
                            </p>
                            {/* The catalog is gated server-side, so an unapproved candidate sees
                                exactly this page: say where the way in is. */}
                            <p>
                                Tests are open to approved candidates.{' '}
                                <Link className="ts-link" to="/tests/application">Check your application</Link>{' '}
                                if you have not applied yet.
                            </p>
                        </div>
                    ) : (
                        <div data-testid="catalog-list">
                            {grouped.map(group => (
                                <section
                                    className="ts-catalog-group"
                                    key={group.key}
                                    data-testid={`catalog-group-${group.key}`}
                                >
                                    {showHeadings && (
                                        <div className="ts-group-head">
                                            <h2 className="ts-panel-title">{group.title}</h2>
                                            <span className="ts-count">{group.rows.length}</span>
                                        </div>
                                    )}
                                    <div className="ts-card-list">
                                        {group.rows.map(cycle => (
                                            <CycleCard
                                                key={cycle.cycle_id}
                                                cycle={cycle}
                                                enrolment={enrolments[cycle.cycle_id]}
                                                now={now}
                                            />
                                        ))}
                                    </div>
                                </section>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default TestCatalog;
